<template>
  <component
    :is="as ?? 'button'"
    :type="isNativeButton ? type : undefined"
    :role="!isNativeButton ? 'button' : undefined"
    :class="mergedClass"
    :disabled="isNativeButton ? disabled || loading : undefined"
    :aria-disabled="!isNativeButton && (disabled || loading) ? true : undefined"
    :aria-busy="loading || undefined"
    v-bind="restAttrs"
    @click="handleClick">
    <component
      v-if="loading"
      :is="LoaderIcon ?? 'span'"
      :size="SIZES[size].iconSize"
      class="tw:animate-spin tw:shrink-0" />
    <component v-else-if="icon" :is="icon" :size="SIZES[size].iconSize" class="tw:shrink-0" />

    <slot v-if="hasContent" />

    <component v-if="trailingIcon && !loading" :is="trailingIcon" :size="SIZES[size].iconSize" class="tw:shrink-0" />
  </component>
</template>

<script setup lang="ts">
// LeadButton — mirrors the LEAD Design System <Button> component 1:1.
// Class strings and structure extracted from
// .claude/skills/migrate-tailwind/design-source/_ds_bundle.js (compiled DS
// runtime; the authoritative source). Don't invent variants or sizes — the
// app's Tailwind theme only compiles utilities the DS uses.
import type { Component } from 'vue';
import { computed, useAttrs, useSlots } from 'vue';
import { IconLoader2 } from '@tabler/icons-vue';
import { cn } from '@/lib/cn';

// Opt out of automatic `$attrs.class` merging so we can route consumer classes
// through `cn()` (tailwind-merge) — that's what lets a caller pass e.g.
// `class="tw:h-6"` and override our `tw:h-7` without needing `!`.
defineOptions({ inheritAttrs: false });

export type LeadButtonVariant =
  'primary' | 'primary-outline' | 'secondary' | 'secondary-outline' | 'white' | 'ghost' | 'danger' | 'link' | 'plain';

export type LeadButtonSize = 'md' | 'sm';

export interface LeadButtonProps {
  variant?: LeadButtonVariant;
  size?: LeadButtonSize;
  /** Leading icon component (renders before the label). */
  icon?: Component;
  /** Trailing icon component (renders after the label). */
  trailingIcon?: Component;
  /** Force pill shape (rounded-full). */
  pill?: boolean;
  /** Full-width. */
  block?: boolean;
  loading?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  /**
   * Render as something other than <button>. Pass RouterLink for internal
   * navigation (preserves right-click, middle-click, href in DOM) or 'a' for
   * external links. Extra props (`to`, `href`, `target`) fall through via
   * $attrs.
   */
  as?: Component | string;
}

const props = withDefaults(defineProps<LeadButtonProps>(), {
  variant: 'primary',
  size: 'md',
  icon: undefined,
  trailingIcon: undefined,
  pill: false,
  block: false,
  loading: false,
  disabled: false,
  type: 'button',
  as: undefined,
});

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const slots = useSlots();
const LoaderIcon = IconLoader2;

// Shared raised shadow used by filled variants (primary, danger, secondary,
// white). Two-part: 1px bottom outer + inset
// 1px bottom line — the subtle "raised chip" effect.
const SHADOW_RAISED = 'tw:shadow-[0_1px_0_rgb(29_39_59/0.04),inset_0_-1px_0_rgb(29_39_59/0.2)]';

// Base classes shared by every variant except `link`. The link variant has
// no border, no padding, no fixed height — it inlines with surrounding text.
const BASE =
  'tw:inline-flex tw:cursor-pointer tw:items-center tw:justify-center tw:border-solid tw:text-center tw:font-medium ' +
  // leading-none — line-height matches font-size so text sits centered in the
  // fixed-height box (h-control/h-7) without the DS type tokens' extra vertical
  // padding pushing the label off-center.
  'tw:leading-none! ' +
  'tw:transition-colors tw:duration-150 tw:ease-in-out ' +
  'tw:focus-visible:outline-none tw:focus-visible:shadow-[0_0_0_4px_rgb(0_122_255/0.1)] ' +
  'tw:disabled:pointer-events-none tw:disabled:opacity-40';

// Two geometry modes per size:
//   - `pad`    — has a label, side padding scales with border/icon
//   - `square` — icon-only, 36×36 (or 28×28) with tighter horizontal padding
//                so the glyph sits centered in a square, not a rectangle
const SIZES: Record<
  LeadButtonSize,
  {
    gap: string;
    text: string;
    height: string;
    iconSize: number;
    pad: Record<'1' | '2', { withIcon: string; noIcon: string }>;
    square: Record<'1' | '2', string>;
  }
> = {
  md: {
    gap: 'tw:gap-3',
    text: 'tw:text-14',
    height: 'tw:h-control',
    iconSize: 16,
    pad: {
      '1': { withIcon: 'tw:px-3 tw:py-1.75', noIcon: 'tw:px-4 tw:py-1.75' },
      '2': { withIcon: 'tw:px-[11px] tw:py-1.5', noIcon: 'tw:px-[15px] tw:py-1.5' },
    },
    // 36x36 square. min-w-control (not fixed w) + shrink-0 keeps the glyph off
    // the edges even when the button sits in a tight flex row.
    square: {
      '1': 'tw:min-w-control tw:shrink-0 tw:px-[9px]',
      '2': 'tw:min-w-control tw:shrink-0 tw:px-2',
    },
  },
  sm: {
    gap: 'tw:gap-2',
    text: 'tw:text-12',
    height: 'tw:h-7',
    iconSize: 14,
    pad: {
      '1': { withIcon: 'tw:px-2 tw:py-1', noIcon: 'tw:px-3 tw:py-1' },
      '2': { withIcon: 'tw:px-[7px] tw:py-[3px]', noIcon: 'tw:px-[11px] tw:py-[3px]' },
    },
    square: {
      '1': 'tw:min-w-7 tw:shrink-0 tw:px-1.5',
      '2': 'tw:min-w-7 tw:shrink-0 tw:px-[5px]',
    },
  },
};

// Each variant declares
// its border width because that drives the padding calculation above.
// IMPORTANT: every class is a literal string — Tailwind's JIT scans source
// files as text and won't compile dynamically built class names.
// Text-colour utilities carry `!` so they beat lithium/Tabler's <a> colour
// reset when the button renders via `as={RouterLink}` / as="a".
const VARIANTS: Record<LeadButtonVariant, { border: 1 | 2; classes: string }> = {
  primary: {
    border: 1,
    classes: `tw:border-transparent tw:bg-primary tw:text-white! ${SHADOW_RAISED} tw:hover:bg-primary/80 tw:active:bg-primary/80`,
  },
  danger: {
    border: 1,
    classes: `tw:border-transparent tw:bg-danger tw:text-white! ${SHADOW_RAISED} tw:hover:bg-danger/80 tw:active:bg-danger/80`,
  },
  secondary: {
    border: 1,
    classes: `tw:border-transparent tw:bg-black tw:text-white! ${SHADOW_RAISED} tw:hover:bg-black/80 tw:active:bg-black/80`,
  },
  'primary-outline': {
    border: 2,
    classes:
      'tw:border-primary tw:bg-transparent tw:text-primary! tw:hover:border-transparent tw:hover:bg-primary tw:hover:text-white! tw:active:bg-primary tw:active:text-white!',
  },
  'secondary-outline': {
    border: 2,
    classes:
      'tw:border-black tw:bg-transparent tw:text-black! tw:hover:border-transparent tw:hover:bg-black tw:hover:text-white! tw:active:bg-black tw:active:text-white!',
  },
  white: {
    border: 1,
    classes: `tw:border-gray-300 tw:bg-white tw:text-body! ${SHADOW_RAISED} tw:hover:bg-gray-50 tw:active:bg-gray-100`,
  },
  ghost: {
    border: 1,
    classes:
      'tw:border-transparent tw:bg-transparent tw:text-body! tw:hover:border-gray-300 tw:active:border-gray-400 tw:active:bg-gray-50',
  },
  // link: bypasses BASE spacing entirely — inline text, no fixed geometry.
  // Keeps underline (that IS the variant); does NOT get the BASE no-underline.
  link: {
    border: 1,
    classes:
      'tw:border-0 tw:bg-transparent tw:p-0 tw:font-semibold tw:text-black! tw:underline! tw:hover:text-gray-700!',
  },
  // plain: no styling at all — inherits color/font from parent context. Meant
  // as an escape hatch for odd spots; the caller customises via `class`.
  plain: {
    border: 1,
    classes: 'tw:border-0 tw:bg-transparent tw:p-0 tw:font-normal',
  },
};

/* --------------------------------- COMPUTED ------------------------------- */

const isBare = computed(() => props.variant === 'link' || props.variant === 'plain');
const hasContent = computed(() => !!slots.default);
const isNativeButton = computed(() => props.as == null || props.as === 'button');

const borderClass = computed(() => {
  if (isBare.value) return '';
  return VARIANTS[props.variant].border === 1 ? 'tw:border' : 'tw:border-2';
});

const isIconOnly = computed(() => !hasContent.value && (!!props.icon || !!props.trailingIcon || props.loading));

const sizeClasses = computed(() => {
  if (isBare.value) return '';
  const s = SIZES[props.size];
  const b = (VARIANTS[props.variant].border === 1 ? '1' : '2') as '1' | '2';
  const hasIcon = !!props.icon || !!props.trailingIcon || props.loading;
  const geometry = isIconOnly.value ? s.square[b] : hasIcon ? s.pad[b].withIcon : s.pad[b].noIcon;
  return `${s.height} ${s.text} ${s.gap} ${geometry}`;
});

// Consumer-supplied class string (via `class="…"`), merged through cn() so
// conflicting utilities the consumer sends win over ours (e.g. `tw:h-6` beats
// our `tw:h-7`) without needing `!`.
const attrs = useAttrs();

const mergedClass = computed(() =>
  cn(
    BASE,
    // link variant keeps its own underline; every other variant strips the
    // <a>/lithium underline reset so as={RouterLink} matches as='button'
    !isBare.value && 'tw:no-underline! tw:hover:no-underline!',
    !isBare.value && (props.pill ? 'tw:rounded-full!' : 'tw:rounded-md!'),
    borderClass.value,
    sizeClasses.value,
    VARIANTS[props.variant].classes,
    props.block && 'tw:w-full',
    // native <button> gets `disabled` attr; other tags do not — dim + block via classes
    !isNativeButton.value && (props.disabled || props.loading) && 'tw:pointer-events-none tw:opacity-40',
    attrs.class as string | undefined
  )
);

// Everything from $attrs except `class` — that's already routed through cn()
// on the root element, and re-binding it would double-apply.
const restAttrs = computed(() => {
  const { class: _c, ...rest } = attrs;
  return rest;
});

/* --------------------------------- METHODS -------------------------------- */

const handleClick = (event: MouseEvent) => {
  if (!props.loading && !props.disabled) {
    emit('click', event);
  }
};
</script>
